import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  TextField,
  Autocomplete,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { BaseTabs } from "components/Shared/BaseTabs";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { addDays } from "date-fns";
import { Download, Visibility } from "@mui/icons-material";

import {
  useLazyGetShipmentPricingReportQuery,
  useLazyGetRegionSalesSummaryQuery,
  useLazyGetRegionDayWiseSalesQuery,
  useDownloadRegionSalesReportExcelMutation,
  useDownloadRegionDayWiseSalesReportExcelMutation,
} from "store/services/reportApi";
import {
  useLazyGetDDLBranchListQuery,
  useLazyGetBranchByRegionDDLQuery,
} from "store/services/branchApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";
import { useLazyGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { useLazyGetDDLCustomerQuery } from "store/services/customerApi";
import { useAuth } from "hooks";

import { useUrlParams } from "hooks/useUrlParams";
import ExportShipmentPricingReport from "./ExportShipmentPricingReport";
import SalesSummary from "./SalesSummary";
import SalesDayWiseGraph from "./VendorReports/SalesDayWiseGraph";

const pricingReportTabs = [
  { label: "Sales Report", value: "all" },
  { label: "Province Summary", value: "regionSummary" },
  { label: "Province Day Summary", value: "regionDaySummary" },
];

const ShipmentPricingReport = () => {
  const { params, appendParams } = useUrlParams();
  const { viewEntity } = useAuth();
  const navigate = useNavigate();

  const activeTab = params.tab || "all";
  const page = Number(params.page) || 1;
  const limit = Number(params.perPage) || 10;
  const keyword = params.search ? params.search.replace(/-/g, "").trim() : "";
  const [errorMessage, setErrorMessage] = React.useState("");

  const [customFromDate, setCustomFromDate] = React.useState(() => {
    const from = params.fromDate;
    return from && moment(from, "MM-DD-YYYY").isValid()
      ? moment(from, "MM-DD-YYYY").toDate()
      : null;
  });
  const [customToDate, setCustomToDate] = React.useState(() => {
    const to = params.toDate;
    return to && moment(to, "MM-DD-YYYY").isValid()
      ? moment(to, "MM-DD-YYYY").toDate()
      : null;
  });
  // Only used for the "custom" range on non-regionDaySummary tabs — set on Apply click.
  // Seeded from the URL so a page load/refresh with an existing custom range still fetches.
  const [appliedFromDate, setAppliedFromDate] = React.useState(() => {
    const from = params.fromDate;
    return from && moment(from, "MM-DD-YYYY").isValid()
      ? moment(from, "MM-DD-YYYY").toDate()
      : null;
  });
  const [appliedToDate, setAppliedToDate] = React.useState(() => {
    const to = params.toDate;
    return to && moment(to, "MM-DD-YYYY").isValid()
      ? moment(to, "MM-DD-YYYY").toDate()
      : null;
  });
  const [region, setRegion] = React.useState(params.region || "all");
  const [branch, setBranch] = React.useState(params.branch || "all");
  const [payer, setPayer] = React.useState(params.payer || "all");
  const [user, setUser] = React.useState(params.user || "all");
  const [responsibleUser, setResponsibleUser] = React.useState(
    params.responsibleUser || "all"
  );
  const [customer, setCustomer] = React.useState(params.customer || "all");
  const [vendor, setVendor] = React.useState(params.vendor || "all");
    const [relationshipManager, setRelationshipManager] = React.useState(
    params.relationshipManager || "all"
  );
  const [paymentStatus, setPaymentStatus] = React.useState(
    params.paymentStatus || "all"
  );

  const [timeRange, setTimeRange] = React.useState(() => {
    const urlTimeRange = params.timeRange;
    return urlTimeRange || "last7days";
  });

  const getDateRange = React.useCallback(
    (range, from, to, isRegionDaySummary = false) => {
      const today = moment().startOf("day");

      if (isRegionDaySummary) {
        switch (range) {
          case "last7days":
            return {
              fromDate: moment()
                .subtract(6, "days")
                .startOf("day")
                .format("MM-DD-YYYY"),
              toDate: today.format("MM-DD-YYYY"),
            };
          case "custom":
            if (from && to && moment(from).isValid() && moment(to).isValid()) {
              const daysDiff = moment(to).diff(moment(from), "days");
              if (daysDiff > 62) {
                return {
                  fromDate: null,
                  toDate: null,
                  error: "Date range cannot exceed 2 months (62 days)."
                };
              }
              return {
                fromDate: moment(from).format("MM-DD-YYYY"),
                toDate: moment(to).format("MM-DD-YYYY"),
              };
            }
            return {
              fromDate: moment()
                .subtract(6, "days")
                .startOf("day")
                .format("MM-DD-YYYY"),
              toDate: today.format("MM-DD-YYYY"),
            };
          default:
            return {
              fromDate: moment()
                .subtract(6, "days")
                .startOf("day")
                .format("MM-DD-YYYY"),
              toDate: today.format("MM-DD-YYYY"),
            };
        }
      }

      switch (range) {
        case "today":
          return {
            fromDate: today.format("MM-DD-YYYY"),
            toDate: today.endOf("day").format("MM-DD-YYYY"),
          };
        case "last7days":
          return {
            fromDate: moment()
              .subtract(6, "days")
              .startOf("day")
              .format("MM-DD-YYYY"),
            toDate: today.endOf("day").format("MM-DD-YYYY"),
          };
        case "oneMonth":
          return {
            fromDate: moment()
              .subtract(1, "month")
              .startOf("day")
              .format("MM-DD-YYYY"),
            toDate: today.endOf("day").format("MM-DD-YYYY"),
          };
        case "custom":
          if (
            from &&
            to &&
            moment(from).isValid() &&
            moment(to).isValid() &&
            moment(to).isAfter(moment(from))
          ) {
            if (moment(to).diff(moment(from), "months", true) > 3) {
              return {
                fromDate: null,
                toDate: null,
                error: "Date range cannot exceed 3 months."
              };
            }
            return {
              fromDate: moment(from).format("MM-DD-YYYY"),
              toDate: moment(to).format("MM-DD-YYYY"),
            };
          }
          return {
            fromDate: moment()
              .subtract(6, "days")
              .startOf("day")
              .format("MM-DD-YYYY"),
            toDate: today.endOf("day").format("MM-DD-YYYY"),
          };
        case "all":
        default:
          return { fromDate: null, toDate: null };
      }
    },
    []
  );

  const getFetchParams = React.useCallback(() => {
    const isRegionDaySummary = activeTab === "regionDaySummary";
    const isPendingCustomSubmit = timeRange === "custom" && !isRegionDaySummary;
    if (isPendingCustomSubmit && (!appliedFromDate || !appliedToDate)) {
      return null;
    }
    const effectiveFromDate = isPendingCustomSubmit ? appliedFromDate : customFromDate;
    const effectiveToDate = isPendingCustomSubmit ? appliedToDate : customToDate;
    const dateResult = getDateRange(
      timeRange,
      effectiveFromDate,
      effectiveToDate,
      isRegionDaySummary
    );

    // Handle error outside of render
    if (dateResult.error) {
      setErrorMessage(dateResult.error);
      return null;
    }

    const { fromDate, toDate } = dateResult;

    if (isRegionDaySummary && (!fromDate || !toDate)) {
      setErrorMessage("Please select a valid date range.");
      return null;
    }
    // Compute paymentStatus conditionally
    let computedPaymentStatus;
    if (activeTab === "regionSummary" || activeTab === "regionDaySummary") {
      computedPaymentStatus = "all";
    } else {
      computedPaymentStatus = paymentStatus !== "all" ? paymentStatus : undefined;
    }
    return {
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(keyword && { keyword }),
      ...(region && region !== "all" && { region }),
      ...(branch && branch !== "all" && { branch }),
      ...(payer && payer !== "all" && { payer }),
      ...(user && user !== "all" && { user }),
      ...(responsibleUser && responsibleUser !== "all" && { responsibleUser }),
      ...(customer && customer !== "all" && { customer }),
      ...(vendor && vendor !== "all" && { vendor }),
      ...(relationshipManager &&
        relationshipManager !== "all" && { relationshipManager }),
      ...(computedPaymentStatus && { paymentStatus: computedPaymentStatus }),
    };
  }, [
    timeRange,
    customFromDate,
    customToDate,
    appliedFromDate,
    appliedToDate,
    keyword,
    region,
    branch,
    payer,
    user,
    responsibleUser,
    customer,
    vendor,
    relationshipManager,
    paymentStatus,
    activeTab,
    getDateRange,
  ]);

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    {
      title: "Sales Report",
    },
  ];

  const [fetchPricingReport, { data: pricingRes, isLoading, isFetching }] =
    useLazyGetShipmentPricingReportQuery();

  const [
    fetchRegionSalesSummary,
    {
      data: regionSummaryData,
      isLoading: isRegionSummaryLoading,
      isFetching: isRegionSummaryFetching,
    },
  ] = useLazyGetRegionSalesSummaryQuery();

  const [
    fetchRegionDayWiseSales,
    {
      data: regionDayWiseData,
      isLoading: isRegionDayWiseLoading,
      isFetching: isRegionDayWiseFetching,
    },
  ] = useLazyGetRegionDayWiseSalesQuery();

  const [downloadRegionExcel] = useDownloadRegionSalesReportExcelMutation();
  const [downloadRegionDayWiseExcel] =
    useDownloadRegionDayWiseSalesReportExcelMutation();

  // DDL option loaders
  const [fetchRegions, { data: regionsData }] = useLazyGetDdlRegionListQuery();
  const [fetchBranches, { data: branchesData, isLoading: isBranchesLoading }] =
    useLazyGetDDLBranchListQuery();
  const [
    fetchBranchesByRegion,
    { data: branchesByRegionData, isLoading: isBranchesByRegionLoading },
  ] = useLazyGetBranchByRegionDDLQuery();
  const [fetchUsers, { data: usersData }] = useLazyGetDDLUserListQuery();
  const [fetchVendors, { data: vendorsData }] =
    useLazyGetDdlVendorProfileQuery();
  const [fetchCustomers, { data: customersData }] =
    useLazyGetDDLCustomerQuery();
  React.useEffect(() => {
    const accountId = viewEntity?.entity?.account?._id;
    const entityId = viewEntity?.entity?._id;
    if (!accountId || !entityId) return;

    fetchRegions({ account: accountId, entity: entityId });
    fetchUsers({ account: accountId, entity: entityId });
    fetchVendors({ account: accountId, entity: entityId });
    fetchCustomers({ account: accountId, entity: entityId });

    if (region === "all") {
      fetchBranches({ account: accountId, entity: entityId });
    } else if (typeof region === "string") {
      fetchBranchesByRegion({
        regionId: region,
        account: accountId,
        entity: entityId,
      });
    }
  }, [
    fetchRegions,
    fetchUsers,
    fetchVendors,
    fetchCustomers,
    fetchBranches,
    fetchBranchesByRegion,
    region,
    viewEntity,
  ]);

  React.useEffect(() => {
    const from = params.fromDate;
    const to = params.toDate;
    const urlTimeRange = params.timeRange || "last7days";

    // Sync all state from URL
    setTimeRange(urlTimeRange);

    if (from && moment(from, "MM-DD-YYYY").isValid()) {
      setCustomFromDate(moment(from, "MM-DD-YYYY").toDate());
    } else {
      setCustomFromDate(null);
    }

    if (to && moment(to, "MM-DD-YYYY").isValid()) {
      setCustomToDate(moment(to, "MM-DD-YYYY").toDate());
    } else {
      setCustomToDate(null);
    }
    setAppliedFromDate(
      from && moment(from, "MM-DD-YYYY").isValid()
        ? moment(from, "MM-DD-YYYY").toDate()
        : null
    );
    setAppliedToDate(
      to && moment(to, "MM-DD-YYYY").isValid()
        ? moment(to, "MM-DD-YYYY").toDate()
        : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.tab]);

  const regionOptions = [
    { label: "All Provinces", value: "all" },
    ...(regionsData?.map((r) => ({
      label: r.label || r.name,
      value: r.value || r._id,
    })) || []),
  ];
  const branchOptions = [
    { label: "All Branches", value: "all" },
    ...(((region === "all" ? branchesData : branchesByRegionData?.data) || []).map(
      (b) => ({
        label: b.label || b.name,
        value: b.value || b._id,
      })
    ) || []),
  ];
  const userOptions = [
    { label: "All Users", value: "all" },
    ...(usersData?.map((u) => ({
      label: u.label || u.name,
      value: u.value || u._id,
    })) || []),
  ];
  const vendorOptions = [
    { label: "All Vendors", value: "all" },
    ...(vendorsData?.map((v) => ({
      label: v.label || v.shopName,
      value: v.value || v._id,
    })) || []),
  ];
  const rmOptions = [
    { label: "All Relationship Managers", value: "all" },
    ...(usersData?.map((u) => ({
      label: u.label || u.name,
      value: u.value || u._id,
    })) || []),
  ];
  const customerOptions = [
    { label: "All Customers", value: "all" },
    ...(customersData?.map((c) => ({
      label: c.label || c.name,
      value: c.value || c._id,
    })) || []),
  ];

  const generateDayColumns = React.useCallback((fromDate, toDate) => {
    const columns = [];
    const start = moment(fromDate, "MM-DD-YYYY");
    const end = moment(toDate, "MM-DD-YYYY");

    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      const dateStr = current.format("YYYY-MM-DD");
      const displayStr = current.format("ddd MM/DD");

      columns.push({
        name: displayStr,
        selector: (row) => {
          const dayData = row.dailySales?.find((d) => d.date === dateStr);
          return dayData?.total || 0;
        },
        cell: (row) => {
          const dayData = row.dailySales?.find((d) => d.date === dateStr);
          const total = dayData?.total || 0;
          const paid = dayData?.paid || 0;
          const notPaid = dayData?.notPaid || 0;
          const collected = dayData?.collected || 0;

          return (
            <Tooltip
              title={
                <div>
                  <div>Paid: Rs. {paid.toFixed(2)}</div>
                  <div>Not Paid: Rs. {notPaid.toFixed(2)}</div>
                  <div>Collected: Rs. {collected.toFixed(2)}</div>
                </div>
              }
            >
              <span>Rs. {total.toFixed(2)}</span>
            </Tooltip>
          );
        },
        width: "200px",
      });

      current.add(1, "day");
    }

    return columns;
  }, []);

  const detailColumns = [
    {
      name: "Shipment",
      cell: (row) => (
        <div className="flex flex-col">
          <div className="text-left">
            <Link
              to={`/app/crm/shipment/${row?.shipment?._id}/detail?tab=pricing`}
              title={row?.shipment?.shipmentId}
              className="text-sm line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
            >
              <HighlightedText
                text={row?.shipment?.shipmentId}
                highlight={keyword}
              />
            </Link>
          </div>
          <div className="mt-1 text-center text-xs text-gray-500 dark:text-gray-300">
            {`${row?.shipment?.status || "N/A"}${row?.deliveryDate ? ` (${row?.deliveryDate})` : ""
              }`}
          </div>
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => (
        <div className="flex flex-col">
          <div className="text-left text-sm">
            {row?.sourceBranch?.name || "N/A"}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-300">
            {row.sourceBranch?.region?.name || "N/A"}
          </div>
        </div>
      ),
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => (
        <div className="flex flex-col">
          <div className="text-left text-sm">
            {row.destinationBranch?.name || "N/A"}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-300">
            {row.destinationBranch?.region?.name || "N/A"}
          </div>
        </div>
      ),
    },
    {
  name: "Relationship Manager",
  selector: (row) => row?.relationshipManager,
  cell: (row) => row?.relationshipManager || "N/A",
},
    {
      name: "Sender",
      selector: (row) => row?.sender ?? "N/A",
    },
    {
      name: "Receiver",
      selector: (row) => row.receiver ?? "N/A",
    },
    {
      name: "Payer",
      selector: (row) => row.payer ?? "N/A",
    },
    {
      name: "Charge",
      selector: (row) => row.charge,
      cell: (row) => (
        <div className="flex flex-col">
          <div className="text-left text-sm">
            Rs. {Number(row.charge || 0).toFixed(2)}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-300">
            {row?.feeItem?.name || "N/A"}
          </div>
        </div>
      ),
    },
    {
      name: "Unit",
      selector: (row) => row.shipment?.weight || row.shipment?.unit?.value,
      cell: (row) => {
        const s = row.shipment;
        if (s?.weight) return `${s.weight.toFixed(2)} kg`;
        if (s?.unit?.value && s?.unit?.unitType?.name) {
          return s.unit.unitType.name.toLowerCase() === "weight"
            ? `${s.unit.value} kg`
            : `${s.unit.value} ${s.unit.unitType.name}`;
        }
        return "N/A";
      },
    },
    {
      name: "Payment Location",
      selector: (row) => row.paymentLocation,
      cell: (row) => (
        <div className="text-center">
          <div>{row.paymentBranch || "N/A"}</div>
          <div className="text-xs text-gray-500 dark:text-gray-300">
            {row.paymentLocation || "N/A"}
          </div>
        </div>
      ),
    },
    {
      name: "Created By",
      selector: (row) => row?.createdBy?.name ?? "N/A",
    },
    {
      name: "Responsible User",
      selector: (row) => row?.responsibleUser?.name ?? "N/A",
    },
    {
      name: "Status",
      selector: (row) =>
        row.chargeType === "Discount" ? row.discountStatus : row.paymentStatus,
      cell: (row) => (
        <Chip
          label={
            (row.chargeType === "Discount"
              ? row.discountStatus
              : row.paymentStatus) || "N/A"
          }
          variant="outlined"
        />
      ),
    },
    {
      name: "Created At",
      selector: (row) => row.shipment?.createdAt,
      cell: (row) => <AppTableDateTime date={row.shipment?.createdAt} />,
    },
  ];

  const regionSummaryColumns = [
    {
      name: "Province Name",
      selector: (row) => row.regionName,
      cell: (row) => row.regionName || "N/A",
    },
    {
      name: "Paid",
      selector: (row) => row.paid,
      cell: (row) => `Rs. ${row.paid?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Not Paid",
      selector: (row) => row.notPaid,
      cell: (row) => `Rs. ${row.notPaid?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Collected",
      selector: (row) => row.collected,
      cell: (row) => `Rs. ${row.collected?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Total",
      selector: (row) => row.total,
      cell: (row) => `Rs. ${row.total?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Action",
      width: "80px",
      cell: (row) => (
        <Tooltip title="View Branch Report for Province">
          <IconButton
            onClick={() => {
              const newParams = new URLSearchParams();
              newParams.set("page", 1);
              newParams.set("perPage", 10);
              newParams.set("region", row._id);
              newParams.set("timeRange", timeRange);
              if (customFromDate && timeRange === "custom") {
                newParams.set(
                  "fromDate",
                  moment(customFromDate).format("MM-DD-YYYY")
                );
              }
              if (customToDate && timeRange === "custom") {
                newParams.set(
                  "toDate",
                  moment(customToDate).format("MM-DD-YYYY")
                );
              }
              navigate(`/app/crm/reports/branch-report?${newParams.toString()}`);
            }}
            color="primary"
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const regionDaySummaryColumns = React.useMemo(() => {
    const { fromDate, toDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate,
      true
    );

    if (!fromDate || !toDate) return [];

    const dayColumns = generateDayColumns(fromDate, toDate);

    return [
      {
        name: "Province Name",
        selector: (row) => row.regionName,
        cell: (row) => row.regionName || "N/A",
        width: "160px",
        sortable: true,
      },
      ...dayColumns,
      {
        name: "Week Total",
        selector: (row) => row.weekTotal,
        cell: (row) => (
          <Box
            component="span"
            sx={{
              fontWeight: row.isTotalRow ? "bold" : "bold",
              color: row.isTotalRow ? "primary.main" : "inherit",
            }}
          >
            Rs. {row.weekTotal?.toFixed(2) || "0.00"}
          </Box>
        ),
        width: "200px",
        sortable: true,
      },
    ];
  }, [timeRange, customFromDate, customToDate, getDateRange, generateDayColumns]);

  React.useEffect(() => {
    const paramsObj = getFetchParams();
    if (!paramsObj) return;

    if (activeTab === "regionSummary") {
      fetchRegionSalesSummary({
        params: {
          page,
          limit,
          account: viewEntity?.entity?.account?._id,
          entity: viewEntity?.entity?._id,
          ...paramsObj,
        }
      });
    } else if (activeTab === "regionDaySummary") {
      fetchRegionDayWiseSales({
        params: {
          page,
          limit,
          account: viewEntity?.entity?.account?._id,
          entity: viewEntity?.entity?._id,
          ...paramsObj,
        }
      });
    } else {
      fetchPricingReport({
        page,
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,
        limit,
        ...paramsObj
      });
    }
  }, [
    fetchPricingReport,
    fetchRegionSalesSummary,
    fetchRegionDayWiseSales,
    page,
    limit,
    getFetchParams,
    viewEntity,
    activeTab,
  ]);

  const handleTabChange = (newTab) => {
    // Navigate with new params, this will cause component to re-read URL params
    const newParams = new URLSearchParams();
    newParams.set('tab', newTab);
    newParams.set('timeRange', 'last7days');
    newParams.set('page', '1');
    newParams.set('paymentStatus',
      newTab === "regionSummary" || newTab === "regionDaySummary" ? "all" : newTab
    );

    navigate(`${window.location.pathname}?${newParams.toString()}`, { replace: true });

    // Force state reset after navigation
    setTimeout(() => {
      setTimeRange("last7days");
      setCustomFromDate(null);
      setCustomToDate(null);
      setAppliedFromDate(null);
      setAppliedToDate(null);
    }, 0);
  };

  const handleDownloadExcel = async () => {
    const isRegionDaySummary = activeTab === "regionDaySummary";
    const { fromDate, toDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate,
      isRegionDaySummary
    );

    if (isRegionDaySummary && (!fromDate || !toDate)) {
      setErrorMessage("Please select a valid 7-day date range.");
      return;
    }

    const apiParams = {
      fromDate,
      toDate,
      ...(keyword && { keyword }),
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      branch: branch !== "all" ? branch : undefined,
      payer: payer !== "all" ? payer : undefined,
      user: user !== "all" ? user : undefined,
      region: region !== "all" ? region : undefined,
      customer: customer !== "all" ? customer : undefined,
      vendor: vendor !== "all" ? vendor : undefined,
      responsibleUser: responsibleUser !== "all" ? responsibleUser : undefined,
      paymentStatus:
        activeTab === "regionSummary" || activeTab === "regionDaySummary"
          ? "all"
          : activeTab !== "all"
            ? activeTab
            : paymentStatus,
      page: 1,
      limit: 10000,
    };

    try {
      let downloadMutation;
      let fileName;

      if (activeTab === "regionSummary") {
        downloadMutation = downloadRegionExcel;
        fileName = `Province_Summary_${moment().format("YYYYMMDD")}.xlsx`;
      } else if (activeTab === "regionDaySummary") {
        downloadMutation = downloadRegionDayWiseExcel;
        const fromDateFormatted = moment(fromDate, "MM-DD-YYYY").format("MMDD");
        const toDateFormatted = moment(toDate, "MM-DD-YYYY").format("MMDD");
        fileName = `Province_Day_Summary_${fromDateFormatted}_to_${toDateFormatted}_${moment().format(
          "YYYYMMDD"
        )}.xlsx`;
      } else {
        setErrorMessage("Excel download only available for Province Summary tabs.");
        return;
      }

      const response = await downloadMutation({ params: apiParams }).unwrap();

      if (!response.blob) {
        throw new Error("No data returned from server");
      }

      const contentType =
        response.blob.type ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      if (!contentType.includes("spreadsheetml.sheet")) {
        throw new Error("Invalid content type received from server");
      }

      const url = window.URL.createObjectURL(response.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
        `Failed to download Excel file. Check filters or server status.`
      );
      console.error("Failed to download Excel:", error);
    }
  };

  const filteredData = React.useMemo(() => {
    let data = [];

    if (activeTab === "regionSummary") {
      data = regionSummaryData?.data || [];
    } else if (activeTab === "regionDaySummary") {
      data = regionDayWiseData?.data || [];

      if (data.length > 0) {
        const { fromDate, toDate } = getDateRange(
          timeRange,
          customFromDate,
          customToDate,
          true
        );

        if (fromDate && toDate) {
          const start = moment(fromDate, "MM-DD-YYYY");
          const end = moment(toDate, "MM-DD-YYYY");

          const dailyTotals = {};
          let grandTotal = 0;

          data.forEach((region) => {
            if (region.dailySales) {
              region.dailySales.forEach((day) => {
                const dayTotal = day.total || 0;
                dailyTotals[day.date] = (dailyTotals[day.date] || 0) + dayTotal;
                grandTotal += dayTotal;
              });
            }
          });

          const totalRowDailySales = [];
          let current = start.clone();
          while (current.isSameOrBefore(end)) {
            const dateStr = current.format("YYYY-MM-DD");
            totalRowDailySales.push({
              date: dateStr,
              total: dailyTotals[dateStr] || 0,
            });
            current.add(1, "day");
          }

          data = [
            ...data,
            {
              _id: "total-row",
              regionName: "TOTAL",
              dailySales: totalRowDailySales,
              weekTotal: grandTotal,
              isTotalRow: true,
            },
          ];
        }
      }
    } else {
      data = pricingRes?.data || [];
    }

    return data;
  }, [
    activeTab,
    regionSummaryData?.data,
    regionDayWiseData?.data,
    pricingRes?.data,
    timeRange,
    customFromDate,
    customToDate,
    getDateRange,
  ]);

  const getRangeDisplay = () => {
    switch (timeRange) {
      case "today":
        return "Today";
      case "last7days":
        return "Last 7 Days";
      case "oneMonth":
        return "One Month";
      case "custom":
        if (customFromDate && customToDate) {
          return `Custom (${moment(customFromDate).format(
            "MM/DD/YYYY"
          )} - ${moment(customToDate).format("MM/DD/YYYY")})`;
        }
        return "Custom (Invalid Dates)";
      case "all":
      default:
        return "All";
    }
  };

  const isCustomRangeValid =
    customFromDate &&
    customToDate &&
    moment(customFromDate).isValid() &&
    moment(customToDate).isValid() &&
    moment(customToDate).isAfter(moment(customFromDate)) &&
    moment(customToDate).diff(moment(customFromDate), "months", true) <= 3;
  const currentLoading =
    isLoading ||
    isFetching ||
    isRegionSummaryLoading ||
    isRegionSummaryFetching ||
    isRegionDayWiseLoading ||
    isRegionDayWiseFetching;

  const summaryMetrics = React.useMemo(() => {
    if (activeTab === "regionSummary") {
      return [
        {
          label: "Total Paid",
          value: regionSummaryData?.totalPaid?.toFixed(2) || "0.00",
        },
        {
          label: "Total Not Paid",
          value: regionSummaryData?.totalNotPaid?.toFixed(2) || "0.00",
        },
        {
          label: "Total Collected",
          value: regionSummaryData?.totalCollected?.toFixed(2) || "0.00",
        },
        {
          label: (
            <Typography color="primary" fontWeight={700} variant="subtitle1">
              Total Sales
            </Typography>
          ),
          value: regionSummaryData?.totalAmount?.toFixed(2) || "0.00",
        },
      ];
    } else if (activeTab === "regionDaySummary") {
      return [
        {
          label: "Total Paid",
          value: regionDayWiseData?.totalPaid?.toFixed(2) || "0.00",
        },
        {
          label: "Total Not Paid",
          value: regionDayWiseData?.totalNotPaid?.toFixed(2) || "0.00",
        },
        {
          label: "Total Collected",
          value: regionDayWiseData?.totalCollected?.toFixed(2) || "0.00",
        },
        {
          label: (
            <Typography color="primary" fontWeight={700} variant="subtitle1">
              Total Sales
            </Typography>
          ),
          value: regionDayWiseData?.totalAmount?.toFixed(2) || "0.00",
        },
      ];
    }
    return [];
  }, [activeTab, regionSummaryData, regionDayWiseData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Paper>
          <BaseTabs
            tabs={pricingReportTabs}
            activeTab={activeTab}
            onChange={handleTabChange}
            actions={
              <>
                {(activeTab === "regionSummary" || activeTab === "regionDaySummary") && (
                  <Tooltip title="Download Excel">
                    <span>
                      <IconButton
                        onClick={handleDownloadExcel}
                        color="primary"
                        disabled={currentLoading || !filteredData.length}
                      >
                        <Download />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {activeTab === "all" && (
                  <ExportShipmentPricingReport
                    timeRange={timeRange}
                    customFromDate={customFromDate}
                    customToDate={customToDate}
                    keyword={keyword}
                    region={region}
                    branch={branch}
                    payer={payer}
                    user={user}
                    responsibleUser={responsibleUser}
                    customer={customer}
                    vendor={vendor}
                    relationshipManager={relationshipManager}
                    paymentStatus={paymentStatus}
                  />
                )}
              </>
            }
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "nowrap",
              px: 2,
              pt: 2,
              gap: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ flexShrink: 0, minWidth: "fit-content", pt: 1 }}>
              {activeTab === "regionSummary"
                ? "Province Summary"
                : activeTab === "regionDaySummary"
                  ? "Province Day Summary"
                  : "Sales Report"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-start",
                gap: 1,
                alignItems: "center",
              }}
            >
              <BaseSelect
                label="Range"
                value={timeRange}
                onChange={(e) => {
                  const newRange = e.target.value;
                  setTimeRange(newRange);
                  appendParams({ timeRange: newRange, page: 1 });

                  const today = moment();
                  if (activeTab === "regionDaySummary") {
                    if (newRange === "last7days") {
                      setCustomToDate(today.toDate());
                      setCustomFromDate(moment().subtract(6, "days").toDate());
                    }
                  } else {
                    if (newRange === "today") {
                      const fd = today.startOf("day").format("MM-DD-YYYY");
                      const td = today.endOf("day").format("MM-DD-YYYY");
                      setCustomFromDate(today.toDate());
                      setCustomToDate(today.toDate());
                      appendParams({ fromDate: fd, toDate: td });
                    } else if (newRange === "last7days") {
                      const fd = moment()
                        .subtract(6, "days")
                        .startOf("day")
                        .format("MM-DD-YYYY");
                      const td = today.format("MM-DD-YYYY");
                      setCustomFromDate(moment(fd, "MM-DD-YYYY").toDate());
                      setCustomToDate(today.toDate());
                      appendParams({ fromDate: fd, toDate: td });
                    } else if (newRange === "oneMonth") {
                      const fd = moment()
                        .subtract(1, "month")
                        .startOf("day")
                        .format("MM-DD-YYYY");
                      const td = today.format("MM-DD-YYYY");
                      setCustomFromDate(moment(fd, "MM-DD-YYYY").toDate());
                      setCustomToDate(today.toDate());
                      appendParams({ fromDate: fd, toDate: td });
                    } else if (newRange === "custom" || newRange === "all") {
                      setCustomFromDate(null);
                      setCustomToDate(null);
                      setAppliedFromDate(null);
                      setAppliedToDate(null);
                      appendParams({ fromDate: undefined, toDate: undefined });
                    }
                  }
                }}
                options={
                  activeTab === "regionDaySummary"
                    ? [
                      { label: "Last 7 Days", value: "last7days" },
                      { label: "Custom (7 Days)", value: "custom" },
                    ]
                    : [
                      { label: "Today", value: "today" },
                      { label: "Last 7 Days", value: "last7days" },
                      { label: "One Month", value: "oneMonth" },
                      { label: "Custom", value: "custom" },
                    ]
                }
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                size="small"
                sx={{ minWidth: 140 }}
              />

              {timeRange === "custom" && (
                <>
                  <DatePicker
                    label="From Date"
                    value={customFromDate}
                    onChange={(val) => {
                      setCustomFromDate(val);
                      if (activeTab === "regionDaySummary") {
                        if (val) {
                          const newToDate = addDays(val, 6);
                          setCustomToDate(newToDate);
                          appendParams({
                            fromDate: val ? moment(val).format("MM-DD-YYYY") : undefined,
                            toDate: moment(newToDate).format("MM-DD-YYYY"),
                            page: 1,
                          });
                        }
                      } else {
                        if (
                          val &&
                          customToDate &&
                          moment(customToDate).diff(moment(val), "months", true) > 3
                        ) {
                          const newToDate = moment(val).add(3, "months").toDate();
                          setCustomToDate(newToDate);
                          setErrorMessage("Date range adjusted to maximum 3 months.");
                          appendParams({
                            fromDate: val ? moment(val).format("MM-DD-YYYY") : undefined,
                            toDate: moment(newToDate).format("MM-DD-YYYY"),
                            page: 1,
                          });
                        } else {
                          appendParams({
                            fromDate: val ? moment(val).format("MM-DD-YYYY") : undefined,
                            page: 1,
                          });
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 140 }} />
                    )}
                    maxDate={
                      activeTab === "regionDaySummary"
                        ? new Date()
                        : customToDate || new Date()
                    }
                    disableFuture
                  />
                  <DatePicker
                    label="To Date"
                    value={customToDate}
                    onChange={(val) => {
                      setCustomToDate(val);
                      if (activeTab === "regionDaySummary") {
                        if (val) {
                          const newFromDate = addDays(val, -6);
                          setCustomFromDate(newFromDate);
                          appendParams({
                            fromDate: moment(newFromDate).format("MM-DD-YYYY"),
                            toDate: val ? moment(val).format("MM-DD-YYYY") : undefined,
                            page: 1,
                          });
                        }
                      } else {
                        if (
                          val &&
                          customFromDate &&
                          moment(val).diff(moment(customFromDate), "months", true) > 3
                        ) {
                          const newFromDate = moment(val)
                            .subtract(3, "months")
                            .toDate();
                          setCustomFromDate(newFromDate);
                          setErrorMessage("Date range adjusted to maximum 3 months.");
                          appendParams({
                            fromDate: moment(newFromDate).format("MM-DD-YYYY"),
                            toDate: val ? moment(val).format("MM-DD-YYYY") : undefined,
                            page: 1,
                          });
                        } else {
                          appendParams({
                            toDate: val ? moment(val).format("MM-DD-YYYY") : undefined,
                            page: 1,
                          });
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 140 }} />
                    )}
                    minDate={
                      activeTab === "regionDaySummary"
                        ? customFromDate
                        : customFromDate
                    }
                    maxDate={new Date()}
                    disableFuture
                    disabled={activeTab === "regionDaySummary"}
                  />
                  {activeTab !== "regionDaySummary" && (
                    <Tooltip
                      title={
                        isCustomRangeValid
                          ? "Apply date range"
                          : "Select a valid range (max 3 months)"
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!isCustomRangeValid}
                          onClick={() => {
                            setAppliedFromDate(customFromDate);
                            setAppliedToDate(customToDate);
                            appendParams({
                              fromDate: moment(customFromDate).format("MM-DD-YYYY"),
                              toDate: moment(customToDate).format("MM-DD-YYYY"),
                              page: 1,
                            });
                          }}
                        >
                          Apply
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}

              <Autocomplete
                size="small"
                options={regionOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={
                  regionOptions.find((opt) => opt.value === region) ||
                  regionOptions[0]
                }
                onChange={(e, v) => {
                  const val = v?.value || "all";
                  setRegion(val);
                  setBranch("all");
                  appendParams({ region: val, branch: "all", page: 1 });
                }}
                disableClearable
                renderInput={(params) => (
                  <TextField {...params} label="Province" />
                )}
                sx={{ minWidth: 160 }}
              />
              {activeTab !== "regionSummary" && activeTab !== "regionDaySummary" && (
                <Autocomplete
                  size="small"
                  options={branchOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  value={
                    branchOptions.find((opt) => opt.value === branch) ||
                    branchOptions[0]
                  }
                  onChange={(e, v) => {
                    const val = v?.value || "all";
                    setBranch(val);
                    appendParams({ branch: val, page: 1 });
                  }}
                  disableClearable
                  disabled={isBranchesLoading || isBranchesByRegionLoading}
                  renderInput={(params) => (
                    <TextField {...params} label="Branch" />
                  )}
                  sx={{ minWidth: 200 }}
                />
              )}
              {activeTab !== "regionSummary" && activeTab !== "regionDaySummary" && (
                <>
                  <Autocomplete
                    size="small"
                    options={[
                      { label: "All", value: "all" },
                      { label: "Paid", value: "Paid" },
                      { label: "Not Paid", value: "Not Paid" },
                      { label: "Collected", value: "Collected" },
                    ]}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    value={
                      [
                        { label: "All", value: "all" },
                        { label: "Paid", value: "Paid" },
                        { label: "Not Paid", value: "Not Paid" },
                        { label: "Collected", value: "Collected" },
                      ].find((opt) => opt.value === paymentStatus) || {
                        label: "All",
                        value: "all",
                      }
                    }
                    onChange={(e, v) => {
                      const val = v?.value || "all";
                      setPaymentStatus(val);
                      appendParams({
                        paymentStatus: val !== "all" ? val : undefined,
                        page: 1,
                      });
                    }}
                    disableClearable
                    renderInput={(params) => (
                      <TextField {...params} label="Payment Status" />
                    )}
                    sx={{ minWidth: 140 }}
                  />
                  <TableFilter
                    searchLabel="Shipment ID"
                    stripHyphen={true}
                    initialFilters={0}
                    onClear={() => {
                      setCustomer("all");
                      setVendor("all");
                      setUser("all");
                      setResponsibleUser("all");
                      setPayer("all");
                      setRelationshipManager("all");
                      appendParams({
                        customer: undefined,
                        vendor: undefined,
                        user: undefined,
                        responsibleUser: undefined,
                        payer: undefined,
                        relationshipManager: undefined,
                        page: 1,
                      });
                    }}
                    filterAlignment="right"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Autocomplete
                        size="small"
                        options={customerOptions}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={
                          customerOptions.find(
                            (opt) => opt.value === customer
                          ) || customerOptions[0]
                        }
                        onChange={(e, v) => {
                          const val = v?.value || "all";
                          setCustomer(val);
                          setVendor("all");
                          appendParams({
                            customer: val !== "all" ? val : undefined,
                            vendor: undefined,
                            page: 1,
                          });
                        }}
                        disableClearable
                        renderInput={(params) => (
                          <TextField {...params} label="Customer Sender" />
                        )}
                        sx={{ minWidth: 368 }}
                      />
                      <Autocomplete
                        size="small"
                        options={vendorOptions}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={
                          vendorOptions.find((opt) => opt.value === vendor) ||
                          vendorOptions[0]
                        }
                        onChange={(e, v) => {
                          const val = v?.value || "all";
                          setVendor(val);
                          setCustomer("all");
                          appendParams({
                            vendor: val !== "all" ? val : undefined,
                            customer: undefined,
                            page: 1,
                          });
                        }}
                        disableClearable
                        renderInput={(params) => (
                          <TextField {...params} label="Vendor Sender" />
                        )}
                        sx={{ minWidth: 368 }}
                      />
                      <Autocomplete
                        size="small"
                        options={userOptions}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={
                          userOptions.find((opt) => opt.value === user) ||
                          userOptions[0]
                        }
                        onChange={(e, v) => {
                          const val = v?.value || "all";
                          setUser(val);
                          appendParams({
                            user: val !== "all" ? val : undefined,
                            page: 1,
                          });
                        }}
                        disableClearable
                        renderInput={(params) => (
                          <TextField {...params} label="Created By" />
                        )}
                        sx={{ minWidth: 368 }}
                      />
                      <Autocomplete
                        size="small"
                        options={userOptions}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={
                          userOptions.find(
                            (opt) => opt.value === responsibleUser
                          ) || userOptions[0]
                        }
                        onChange={(e, v) => {
                          const val = v?.value || "all";
                          setResponsibleUser(val);
                          appendParams({
                            responsibleUser: val !== "all" ? val : undefined,
                            page: 1,
                          });
                        }}
                        disableClearable
                        renderInput={(params) => (
                          <TextField {...params} label="Responsible User" />
                        )}
                        sx={{ minWidth: 368 }}
                      />
                      <Autocomplete
                        size="small"
                        options={[
                          { label: "All", value: "all" },
                          { label: "Sender", value: "Sender" },
                          { label: "Receiver", value: "Receiver" },
                        ]}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={
                          [
                            { label: "All", value: "all" },
                            { label: "Sender", value: "Sender" },
                            { label: "Receiver", value: "Receiver" },
                          ].find((opt) => opt.value === payer) || {
                            label: "All",
                            value: "all",
                          }
                        }
                        onChange={(e, v) => {
                          const val = v?.value || "all";
                          setPayer(val);
                          appendParams({
                            payer: val !== "all" ? val : undefined,
                            page: 1,
                          });
                        }}
                        disableClearable
                        renderInput={(params) => (
                          <TextField {...params} label="Payer" />
                        )}
                        sx={{ minWidth: 180 }}
                      />
                      <Autocomplete
                        size="small"
                        options={rmOptions}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={
                          rmOptions.find((opt) => opt.value === relationshipManager) ||
                          rmOptions[0]
                        }
                        onChange={(e, v) => {
                          const val = v?.value || "all";
                          setRelationshipManager(val);
                          appendParams({
                            relationshipManager: val !== "all" ? val : undefined,
                            page: 1,
                          });
                        }}
                        disableClearable
                        renderInput={(params) => (
                          <TextField {...params} label="Relationship Manager" />
                        )}
                        sx={{ minWidth: 368 }}
                      />
                    </Box>
                  </TableFilter>
                </>
              )}
            </Box>
          </Box>

          {activeTab === "regionSummary" || activeTab === "regionDaySummary" ? (
            <>
              {summaryMetrics.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                    gap: 2,
                    alignItems: "center",
                    px: 2,
                    pt: 2,
                  }}
                >
                  <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
                    <Box>
                      <Typography variant="body2" fontWeight="normal">
                        {`${getRangeDisplay()} All Provinces ${activeTab === "regionDaySummary" ? "Day-wise" : ""
                          } Summary:`}
                      </Typography>
                    </Box>
                    {summaryMetrics.map((metric, index) => (
                      <Box key={index}>
                        <Typography variant="body2" color="text.secondary">
                          {metric.label}
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          Rs. {metric.value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
              <AppTable
                columns={
                  activeTab === "regionDaySummary"
                    ? regionDaySummaryColumns
                    : regionSummaryColumns
                }
                data={filteredData}
                progressPending={currentLoading}
                paginationServer
                paginationPerPage={limit}
                paginationTotalRows={
                  activeTab === "regionDaySummary"
                    ? regionDayWiseData?.totalItems ?? 0
                    : regionSummaryData?.totalItems ?? 0
                }
                paginationDefaultPage={page}
                onChangePage={(newPage) => appendParams({ page: newPage })}
                onChangeRowsPerPage={(newPerPage) =>
                  appendParams({ perPage: newPerPage, page: 1 })
                }
              />
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  gap: 2,
                  alignItems: "center",
                  px: 2,
                  pt: 2,
                }}
              >
                <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body2" fontWeight="normal">
                      {`${getRangeDisplay()} Sales Summary:`}
                    </Typography>
                  </Box>
                  <SalesSummary
                    timeRange={timeRange}
                    customFromDate={
                      timeRange === "custom" ? appliedFromDate : customFromDate
                    }
                    customToDate={
                      timeRange === "custom" ? appliedToDate : customToDate
                    }
                    keyword={keyword}
                    account={viewEntity?.entity?.account?._id}
                    entity={viewEntity?.entity?._id}
                    branch={branch}
                    payer={payer}
                    user={user}
                    responsibleUser={responsibleUser}
                    region={region}
                    customer={customer}
                    vendor={vendor}
                    relationshipManager={relationshipManager}
                    paymentStatus={
                      activeTab !== "all" ? activeTab : paymentStatus
                    }
                  />
                </Stack>
              </Box>

              <hr className="my-2" />

              <AppTable
                columns={detailColumns}
                data={filteredData}
                progressPending={currentLoading}
                paginationTotalRows={pricingRes?.totalItems || 0}
              />
            </>
          )}

          <Snackbar
            open={!!errorMessage}
            autoHideDuration={6000}
            onClose={() => setErrorMessage("")}
          >
            <Alert severity="error" onClose={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          </Snackbar>
        </BreadcrumbLayout.Paper>

        {activeTab === "regionDaySummary" && (
          <SalesDayWiseGraph
            account={viewEntity?.entity?.account?._id}
            entity={viewEntity?.entity?._id}
            branch={branch}
            user={user}
            payer={payer}
            customer={customer}
            vendor={vendor}
            responsibleUser={responsibleUser}
            region={region}
            paymentStatus="all"
            fromDate={
              getDateRange(timeRange, customFromDate, customToDate, true)
                .fromDate
            }
            toDate={
              getDateRange(timeRange, customFromDate, customToDate, true)
                .toDate
            }
          />
        )}
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default ShipmentPricingReport;
